import Form from "@/components/Form";
import { useTranslation } from "react-i18next";

import Field from "@/components/Field";
import Fieldset from "@/components/Fieldset";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";

import { isRequired } from "@/lib/form";
import React, { memo, useMemo } from "react";
import SaveButton from "@/components/SaveButton";
import PageContent from "@/components/PageContent";
import AsyncButton from "@/components/AsyncButton";
import { PenLine } from "lucide-react";
import { useDefaultRates } from "@/modules/defaultRates/hooks/useDefaultRates";
import SettlementTypeSelect from "@/modules/projects/components/SettlementTypeSelect";
import { NumberInput } from "@/components/ui/NumberInput";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SETTLEMENT_TYPE_HOURLY_RATE } from "@/schemas/commonFields/settlementType";
import {
  ReadDepartmentBasedProjectRates,
  WriteDepartmentBasedProjectRates,
} from "@/schemas/departmentBasedProjectRates";
import PayrollDepartmentSelect from "@/components/PayrollDepartmentSelect";
import StoreSelect from "@/components/StoreSelect";
import { useDepartmentBasedProjectRatesDictionary } from "@/modules/departmentBasedProjectRates/hooks/useDepartmentBasedProjectRatesDictionary";
import { useDictionaryContext } from "@/components/dictionaries/DictionaryContext";
import DatePicker from "@/components/DatePicker";
import { DepartmentBasedProjectSettlementTypesService } from "@/modules/departmentBasedProjectRates/service/departmentBasedProjectSettlementTypesService";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";

const DepartmentBasedProjectRatesFormComponent = () => {
  const { t } = useTranslation();
  const { id } = useDictionaryContext();
  const { can_update, can_create } = useModulePermissions();
  const hasPermission = id === "new" ? can_create : can_update;
  const config = useDepartmentBasedProjectRatesDictionary();
  const { Icon, ns } = config;
  const labels = config.getLabels(t);

  const { mutation, form, query } = useDictionaryContext<
    ReadDepartmentBasedProjectRates,
    WriteDepartmentBasedProjectRates
  >();

  const pageTitle = id === "new" ? labels.new : t(`${ns}.edit`);

  const payrollDepartmentId = form.watch("payroll_department_id");
  const storeId = form.watch("store_id");
  const settlementType = form.watch("settlement_type");
  const validFrom = form.getValues("valid_from");

  useFormUnsavedChangesWarning(form);

  const isHourlyRate = settlementType === SETTLEMENT_TYPE_HOURLY_RATE;
  const canFetchDefaultRates =
    hasPermission && isHourlyRate && !!payrollDepartmentId && !!storeId;

  const {
    applyDefaultRates,
    defaultRatesValidFromDate,
    isLoading: defaultRatesLoading,
  } = useDefaultRates({
    form,
    settlementType,
    validFrom,
    enabled: canFetchDefaultRates,
  });
  const isButtonDisabled =
    defaultRatesLoading ||
    !hasPermission ||
    !payrollDepartmentId ||
    !storeId ||
    !settlementType ||
    !isHourlyRate;

  const tooltipMessage = useMemo(() => {
    if (!hasPermission) return null;
    if (settlementType && !isHourlyRate) {
      return t(`${ns}.buttons.tooltipOnlyHourlyRate`);
    }
    const missing: string[] = [];
    if (!payrollDepartmentId)
      missing.push(t(`${ns}.fields.payroll_department_id`));
    if (!storeId) missing.push(t(`${ns}.fields.store_id`));
    if (!settlementType) missing.push(t(`${ns}.fields.settlement_type`));
    if (missing.length > 0) {
      return t(`${ns}.buttons.tooltipMissingFields`, {
        fields: missing.join(", "),
      });
    }
    return null;
  }, [
    hasPermission,
    settlementType,
    isHourlyRate,
    payrollDepartmentId,
    storeId,
    t,
    ns,
  ]);

  return (
    <Form
      form={form}
      handleSubmit={async (values) => await mutation.mutateAsync(values)}
    >
      <PageContent
        icon={<Icon className="w-6 h-6 mr-3" />}
        title={pageTitle}
        footer={<SaveButton />}
      >
        <Fieldset className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Field
            name="payroll_department_id"
            label={t(`${ns}.fields.payroll_department_id`)}
            required={isRequired(config.schema.shape.payroll_department_id)}
            render={({ field }) => (
              <PayrollDepartmentSelect
                {...field}
                className="w-80"
                defaultItem={{
                  id: query?.data?.payroll_department_id,
                  name: query?.data?.payroll_department_name,
                }}
              />
            )}
          />
          <Field
            name="store_id"
            label={t(`${ns}.fields.store_id`)}
            required={isRequired(config.schema.shape.store_id)}
            render={({ field }) => (
              <StoreSelect
                {...field}
                className="w-80"
                defaultItem={{
                  id: query?.data?.store_id,
                  name: query?.data?.store_name,
                }}
              />
            )}
          />
        </Fieldset>
        <div className="space-y-4">
          <Fieldset className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Field
              name="settlement_type"
              label={t(`${ns}.fields.settlement_type`)}
              required={isRequired(config.schema.shape.settlement_type)}
              className="space-y-2"
              render={({ field }) => (
                <SettlementTypeSelect
                  className="w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  settlementTypeService={
                    DepartmentBasedProjectSettlementTypesService
                  }
                  {...field}
                  defaultItem={{
                    id: field.value,
                    name: field.value
                      ? t(`settlementType.${field.value}`)
                      : undefined,
                  }}
                />
              )}
            />
            <div className="grid grid-cols-2 gap-4 items-end">
              <div className="min-w-0 overflow-hidden">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-block">
                        <AsyncButton
                          className="w-auto max-w-full"
                          labelClassName="overflow-hidden text-ellipsis whitespace-nowrap"
                          type="button"
                          disabled={isButtonDisabled}
                          loadingLabel={t(`${ns}.buttons.loadingDefaultRates`)}
                          label={t(`${ns}.buttons.applyDefaultRates`)}
                          loading={defaultRatesLoading}
                          icon={<PenLine className="w-6 h-6 mr-3" />}
                          onClick={applyDefaultRates}
                        />
                      </span>
                    </TooltipTrigger>
                    {tooltipMessage && (
                      <TooltipContent>{tooltipMessage}</TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
              <Field
                name="valid_from"
                label={t(`${ns}.fields.valid_from`)}
                required={isRequired(config.schema.shape.valid_from)}
                render={({ field }) => (
                  <DatePicker className="w-full" {...field} />
                )}
              />
            </div>
          </Fieldset>
          {defaultRatesValidFromDate && (
            <div className="text-sm mt-1 text-green-600">
              {t(`${ns}.defaultRatesFromDate`, {
                date: defaultRatesValidFromDate,
              })}
            </div>
          )}
          <Fieldset className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Field
              name="rate_student_net"
              label={t(`${ns}.fields.rate_student_net`)}
              required={isRequired(config.schema.shape.rate_student_net)}
              render={({ field }) => <NumberInput {...field} />}
            />
            <Field
              name="rate_student_tax"
              label={t(`${ns}.fields.rate_student_tax`)}
              required={isRequired(config.schema.shape.rate_student_tax)}
              render={({ field }) => <NumberInput {...field} />}
            />
          </Fieldset>

          <Fieldset className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Field
              name="rate_kch_net"
              label={t(`${ns}.fields.rate_kch_net`)}
              required={isRequired(config.schema.shape.rate_kch_net)}
              render={({ field }) => <NumberInput {...field} />}
            />
            <Field
              name="rate_kch_tax"
              label={t(`${ns}.fields.rate_kch_tax`)}
              required={isRequired(config.schema.shape.rate_kch_tax)}
              render={({ field }) => <NumberInput {...field} />}
            />
          </Fieldset>

          <Fieldset className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Field
              name="rate_zus_net"
              label={t(`${ns}.fields.rate_zus_net`)}
              required={isRequired(config.schema.shape.rate_zus_net)}
              render={({ field }) => <NumberInput {...field} />}
            />
            <Field
              name="rate_zus_tax"
              label={t(`${ns}.fields.rate_zus_tax`)}
              required={isRequired(config.schema.shape.rate_zus_tax)}
              render={({ field }) => <NumberInput {...field} />}
            />
          </Fieldset>

          <Fieldset className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Field
              name="rate_zus_sick_net"
              label={t(`${ns}.fields.rate_zus_sick_net`)}
              required={isRequired(config.schema.shape.rate_zus_sick_net)}
              render={({ field }) => <NumberInput {...field} />}
            />
            <Field
              name="rate_zus_sick_tax"
              label={t(`${ns}.fields.rate_zus_sick_tax`)}
              required={isRequired(config.schema.shape.rate_zus_sick_tax)}
              render={({ field }) => <NumberInput {...field} />}
            />
          </Fieldset>

          <Fieldset className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Field
              name="rate_gross"
              label={t(`${ns}.fields.rate_gross`)}
              required={isRequired(config.schema.shape.rate_gross)}
              render={({ field }) => <NumberInput {...field} />}
            />
          </Fieldset>
        </div>
      </PageContent>
    </Form>
  );
};

export const DepartmentBasedProjectRatesForm = memo(
  DepartmentBasedProjectRatesFormComponent,
);
